/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */

import alfrescoNodeService from "./AlfrescoNodeService.js";
import workspaceService from "./WorkspaceService.js";
import { nodesApi } from "../AlfrescoApi.js";
import { useUploadStore } from "@/stores/upload.js";

/**
 * Service for creating files with classification plan and requalifying documents.
 * Centralizes business logic for folder hierarchy creation, file creation with plan,
 * and document requalification.
 */
class ClassificationFileService {
  /**
   * Ensures the folder hierarchy exists under the given parent.
   * For each folder in pathFull, attempts to create it. If it already exists (409),
   * retrieves the existing folder ID.
   * @param {string} parentId - The parent node ID to start from
   * @param {Object[]} pathFull - Array of folder objects { name, type, properties }
   * @returns {Promise<string>} The ID of the last folder in the hierarchy
   */
  async ensureFolderHierarchy(parentId, pathFull) {
    let currentParentId = parentId;

    for (const folder of pathFull) {
      const opts = {
        name: folder.name,
        nodeType: folder.type || "cm:folder",
      };

      if (folder.properties) {
        opts.properties = folder.properties;
      }

      try {
        const created = await alfrescoNodeService.createNode(
          currentParentId,
          opts,
        );
        currentParentId = created.entry.id;
      } catch (error) {
        // If folder already exists (409 Conflict), find the existing one
        // by relative path from the current parent.
        // PristyError stores HTTP status in error.code, not error.status.
        const statusCode = error.code || error.status;
        if (statusCode === 409) {
          try {
            const existingNode = await nodesApi.getNode(currentParentId, {
              relativePath: folder.name,
            });
            currentParentId = existingNode.entry.id;
          } catch {
            throw error;
          }
        } else {
          throw error;
        }
      }
    }

    return currentParentId;
  }

  /**
   * Gets the documentLibrary node ID for a workspace.
   * @param {string} workspaceSiteId - The site ID of the workspace
   * @returns {Promise<string>} The documentLibrary node ID
   */
  async getDocumentLibraryId(workspaceSiteId) {
    const workspace = await workspaceService.getWorkspace(workspaceSiteId);
    const docLib = await alfrescoNodeService.getDocumentLibrary(workspace.guid);
    return docLib.id;
  }

  /**
   * Creates a file with classification plan by copying a template, placing it
   * in the correct folder hierarchy, and applying metadata.
   * @param {string} workspaceSiteId - The workspace site ID
   * @param {Object[]} pathFull - Folder hierarchy to create
   * @param {string} templateNodeId - Template node ID to copy
   * @param {string} fileName - Final file name
   * @param {Object} metadata - Metadata properties to apply
   * @param {Object} nature - Nature object with type and aspects
   * @returns {Promise<Object>} The created node
   */
  async createFileWithPlan(
    workspaceSiteId,
    pathFull,
    templateNodeId,
    fileName,
    metadata,
    nature,
  ) {
    // Get the document library root
    const docLibId = await this.getDocumentLibraryId(workspaceSiteId);

    // Create folder hierarchy
    const targetFolderId = await this.ensureFolderHierarchy(docLibId, pathFull);

    // Copy template to the target folder
    const copiedNode = await alfrescoNodeService.copyNode(
      templateNodeId,
      targetFolderId,
      fileName,
    );

    // Update node with type, aspects, and metadata
    const nodeBodyUpdate = {};

    if (nature.type) {
      nodeBodyUpdate.nodeType = nature.type;
    }

    if (nature.aspects) {
      nodeBodyUpdate.aspectNames = nature.aspects;
    }

    if (metadata && Object.keys(metadata).length > 0) {
      nodeBodyUpdate.properties = metadata;
    }

    if (Object.keys(nodeBodyUpdate).length > 0) {
      await alfrescoNodeService.updateNode(copiedNode.entry.id, nodeBodyUpdate);
    }

    return copiedNode;
  }

  /**
   * Requalifies an existing document by moving it to the correct folder hierarchy
   * and updating its metadata/type.
   * @param {string} nodeId - The document node ID to requalify
   * @param {string} workspaceSiteId - The workspace site ID
   * @param {Object[]} pathFull - New folder hierarchy
   * @param {Object} metadata - New metadata properties
   * @param {string} newFileName - New file name (optional)
   * @param {Object} nature - Nature object with type and aspects
   * @returns {Promise<Object>} The updated node
   */
  async requalifyDocument(
    nodeId,
    workspaceSiteId,
    pathFull,
    metadata,
    newFileName,
    nature,
    destinationNodeId,
  ) {
    // Use destination node as root, or fall back to document library
    const rootId =
      destinationNodeId || (await this.getDocumentLibraryId(workspaceSiteId));

    // Create folder hierarchy
    const targetFolderId = await this.ensureFolderHierarchy(rootId, pathFull);

    // Move the document to the target folder
    await alfrescoNodeService.moveNode(nodeId, targetFolderId, newFileName);

    // Update node with aspects and metadata
    // Type is not changed here — the injector backend handles type assignment
    // during upload. Requalification only moves and updates metadata.
    const nodeBodyUpdate = {};

    if (nature.aspects) {
      const existingNode = await alfrescoNodeService.getNode(nodeId);
      const existingAspects = existingNode.aspectNames || [];
      const newAspects = new Set([...existingAspects, ...nature.aspects]);
      nodeBodyUpdate.aspectNames = [...newAspects];
    }

    if (metadata && Object.keys(metadata).length > 0) {
      nodeBodyUpdate.properties = metadata;
    }

    if (Object.keys(nodeBodyUpdate).length > 0) {
      return alfrescoNodeService.updateNode(nodeId, nodeBodyUpdate);
    }
  }

  /**
   * Uploads files with metadata according to a classification plan using the injector.
   * Delegates to the upload store to leverage worker-based implementation for better
   * performance and progress tracking.
   * @param {string} workspaceSiteId The site ID of the workspace (e.g., "my-workspace").
   * @param {Object[]} pathFull Array of folder objects with name, type, and properties for path.full.
   * @param {File[]} files The files to upload.
   * @param {Object} metadata The metadata object to apply to the uploaded files.
   * @param {Object[]} fileRenames Array of objects with original and new names.
   */
  async uploadWithPlan(
    workspaceSiteId,
    pathFull,
    files,
    metadata,
    fileRenames,
    destinationNodeId,
  ) {
    const uploadStore = useUploadStore();
    uploadStore.startUploadWithClassificationPlan(
      workspaceSiteId,
      pathFull,
      files,
      metadata,
      fileRenames,
      destinationNodeId,
    );
  }
}

export default new ClassificationFileService();
